// @ts-expect-error no type bindings provided
import { formatMoney } from "@shopify/theme-currency"

// TODO move this to types.ts
interface Response {
  product: {
    variants: {
      id: number
      compare_at_price: string
      price: string
    }[]
  }
}

/**
 * Fetches the presentment price for a product variant and formats it.
 *
 * @param productHandle The handle of the product
 * @param fetchListPrice Fetch the list price (aka compare_at_price). Otherwise fetch the price.
 * @param [variantId] The product's variant whose price should be fetched.
 * The product's first variant is used if this is not provided.
 * @returns {Promise} A promise containing the product variant's formatted presentment price
 */
export async function fetchPresentmentPrice(
  productHandle: string,
  fetchListPrice?: boolean,
  variantId?: string
): Promise<string> {
  const response = await fetch(`/products/${productHandle}.json`)
  if (!response.ok) {
    throw new Error(`Failed to fetch product: [${response.status}] ${response.statusText}`)
  }
  const json = await response.json<Response>()
  const variants = json.product.variants
  const variant = variants.find(v => (variantId ? parseInt(variantId, 10) === v.id : true))!
  const price = fetchListPrice ? variant.compare_at_price : variant.price
  return formatMoney(price, typeof window.Shopify === "object" ? window.Shopify.money_format : undefined)
}

/**
 * Updates all elements specified by priceElementSelector with the product's presentment price.
 *
 * @param priceElementSelector The CSS selector used to find elements where
 * the presentment price should be inserted
 * @param productTitleAttribute The attribute name of the element that contains
 * the product handle or title
 * @return {Promise} A promise that resolves when all elements' prices have been updated
 * @deprecated Use Nosto.setPresentmentPricesUrl() instead.
 */
export function setPresentmentPrices(priceElementSelector: string, productTitleAttribute: string) {
  const elements = document.querySelectorAll(priceElementSelector)
  const promises = Array.from(elements).map(async element => {
    const productTitle = element.getAttribute(productTitleAttribute)
    if (!productTitle) {
      return Promise.reject(new Error(`${productTitleAttribute} not found: ${element.outerHTML}`))
    }

    const productHandle = productTitle
      .replace(/[^\w-]/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase()

    const price = await fetchPresentmentPrice(productHandle)
    return element.replaceChildren(document.createTextNode(price))
  })
  return Promise.all(promises)
}

/**
 * Updates all elements specified by priceElement with the product's presentment price.
 *
 * @param priceElement The CSS selector used to find elements where
 * the presentment price should be inserted
 * @param productUrl The attribute name of the element that contains the product URL
 * @param fetchListPrice Fetch the list price (aka compare_at_price). Otherwise fetch the price.
 * @param [variantId] The attribute name of the element that contains the variant id.
 * @return {Promise} A promise that resolves when all elements' prices have been updated
 */
export function setPresentmentPricesUrl(
  priceElement: string,
  productUrl: string,
  fetchListPrice: boolean,
  variantId: string
) {
  const elements = document.querySelectorAll(priceElement)
  const promises = Array.from(elements).map(async element => {
    const productUrlAttribute = element.getAttribute(productUrl)
    if (!productUrlAttribute) {
      return Promise.reject(new Error(`${productUrl} not found: ${element.outerHTML}`))
    }

    const productHandle = new URL(productUrlAttribute).pathname.substring("/products/".length)
    const variantIdAttribute = element.getAttribute(variantId)
    const price = await fetchPresentmentPrice(productHandle, fetchListPrice, variantIdAttribute!)
    return element.replaceChildren(document.createTextNode(price))
  })
  return Promise.all(promises)
}
